import type { ReturnInwardHeader, ReturnOrderInfo, ReturnOrderLine, ReturnSerialRow } from "../types";
import { normalizeSerial } from "../utils/serial";

/** Toàn bộ dữ liệu phiếu nhập đổi trả nằm trong một state; mọi thay đổi đi qua reducer */
export interface ReturnInwardState {
  header: ReturnInwardHeader;
  order: ReturnOrderInfo;
  orderLines: ReturnOrderLine[];
  /** Serial đã quét, theo thứ tự quét. Serial là khoá duy nhất của dòng */
  rows: ReturnSerialRow[];
  /** Id phiếu sau lần lưu đầu tiên; 0 khi chưa lưu */
  transactionId: number;
}

/** Vị trí gán cho dòng serial: id gửi lên (locatorId) và mã / tên để hiện trên lưới */
export interface RowPosition {
  id: number | null;
  code: string | null;
  name: string | null;
}

export type ReturnInwardAction =
  | {
      type: "LOAD_TEMPLATE";
      order: ReturnOrderInfo;
      orderLines: ReturnOrderLine[];
      headerPatch: Partial<ReturnInwardHeader>;
    }
  | {
      type: "LOAD_DETAIL";
      header: ReturnInwardHeader;
      order: ReturnOrderInfo;
      orderLines: ReturnOrderLine[];
      rows: ReturnSerialRow[];
    }
  | { type: "SET_DESCRIPTION"; value: string }
  /** Đổi kho nhập: vị trí của các serial thuộc kho cũ nên bỏ, như trang cũ */
  | { type: "SET_WAREHOUSE"; invId: number | null }
  /** Vị trí mặc định: áp cho serial quét sau và mọi serial đang có, như trang cũ */
  | { type: "SET_DEFAULT_POSITION"; position: RowPosition }
  | { type: "ADD_ROWS"; rows: ReturnSerialRow[] }
  | { type: "REMOVE_ROWS"; serials: string[] }
  | { type: "SET_ROW_POSITION"; serial: string; position: RowPosition }
  | { type: "SET_ROW_FIELD"; serial: string; key: "serialStatus" | "attribute11" | "note"; value: string | null }
  | { type: "SET_ROW_QUANTITY"; serial: string; quantity: number }
  | { type: "SET_ALL_STATUS"; status: string }
  | { type: "SAVED"; transactionId: number }
  | { type: "LOCKED"; transactionId: number; finishDate: string };

export const createInitialState = (sourceType: string, sourceCode: string, departmentId: number | null): ReturnInwardState => ({
  header: {
    transactionId: 0,
    transactionCode: "",
    transactionDate: "",
    sourceCode,
    sourceType,
    subInvId: null,
    toPositionId: null,
    departmentId,
    status: "NEW",
    description: "",
  },
  order: {},
  orderLines: [],
  rows: [],
  transactionId: 0,
});

const rowKey = (row: ReturnSerialRow) => normalizeSerial(row.serial);

const withPosition = (row: ReturnSerialRow, position: RowPosition): ReturnSerialRow => ({
  ...row,
  locatorId: position.id,
  positionCode: position.code,
  positionName: position.name,
});

const NO_POSITION: RowPosition = { id: null, code: null, name: null };

const updateRow = (state: ReturnInwardState, serial: string, update: (row: ReturnSerialRow) => ReturnSerialRow) => {
  const key = normalizeSerial(serial);
  return { ...state, rows: state.rows.map((row) => (rowKey(row) === key ? update(row) : row)) };
};

export const returnInwardReducer = (state: ReturnInwardState, action: ReturnInwardAction): ReturnInwardState => {
  switch (action.type) {
    case "LOAD_TEMPLATE":
      return {
        ...state,
        order: action.order,
        orderLines: action.orderLines,
        header: { ...state.header, ...action.headerPatch },
      };

    case "LOAD_DETAIL":
      return {
        ...state,
        // Kho của phiếu đã lưu backend trả ở toInvId; gửi lên bằng subInvId
        header: { ...action.header, subInvId: action.header.subInvId ?? action.header.toInvId ?? null },
        order: action.order,
        orderLines: action.orderLines,
        rows: action.rows,
        transactionId: Number(action.header.transactionId) || 0,
      };

    case "SET_DESCRIPTION":
      return { ...state, header: { ...state.header, description: action.value } };

    case "SET_WAREHOUSE":
      if (state.header.subInvId === action.invId) return state;
      return {
        ...state,
        header: { ...state.header, subInvId: action.invId, toInvId: action.invId, toPositionId: null },
        rows: state.rows.map((row) => withPosition(row, NO_POSITION)),
      };

    case "SET_DEFAULT_POSITION":
      return {
        ...state,
        header: { ...state.header, toPositionId: action.position.id },
        rows: action.position.id == null ? state.rows : state.rows.map((row) => withPosition(row, action.position)),
      };

    case "ADD_ROWS": {
      // Chặn trùng ngay trong reducer, phòng khi hai lượt quét cùng serial về gần nhau
      const existing = new Set(state.rows.map(rowKey));
      const added: ReturnSerialRow[] = [];
      action.rows.forEach((row) => {
        const key = rowKey(row);
        if (!key || existing.has(key)) return;
        existing.add(key);
        added.push(row);
      });
      return added.length ? { ...state, rows: [...state.rows, ...added] } : state;
    }

    case "REMOVE_ROWS": {
      const toRemove = new Set(action.serials.map(normalizeSerial));
      const rows = state.rows.filter((row) => !toRemove.has(rowKey(row)));
      return rows.length === state.rows.length ? state : { ...state, rows };
    }

    case "SET_ROW_POSITION":
      return updateRow(state, action.serial, (row) => withPosition(row, action.position));

    case "SET_ROW_FIELD":
      return updateRow(state, action.serial, (row) => ({ ...row, [action.key]: action.value }));

    case "SET_ROW_QUANTITY":
      return updateRow(state, action.serial, (row) => ({ ...row, primaryQuantity: action.quantity }));

    case "SET_ALL_STATUS":
      return { ...state, rows: state.rows.map((row) => ({ ...row, serialStatus: action.status })) };

    case "SAVED":
      return {
        ...state,
        transactionId: action.transactionId || state.transactionId,
        header: { ...state.header, status: "OPEN" },
      };

    case "LOCKED":
      return {
        ...state,
        transactionId: action.transactionId || state.transactionId,
        header: { ...state.header, status: "LOCK", finishDate: action.finishDate },
      };

    default:
      return state;
  }
};
